<template>
  <div class="loader">
    <div class="loader__logo">
      <ComposedLogo />
    </div>

    <div class="loader__age">
      <div class="loader__age-label">
        <span v-html="$t('ageDisclaimer')"></span>
      </div>
      <div class="loader__age-input" style="display: none">
        <input
          type="number"
          :max="new Date().getFullYear()"
          min="1900"
          placeholder="YEAR"
          pattern="[0-9]{4}"
          v-model="age"
          @keypress="(event) => { if(event.keyCode === 13) enter(); }"
        >
      </div>

      <div class="buttons">
        <button class="loader__age-enter" @click="enter">{{ $t('ageButton') }}</button>
        <button class="loader__age-enter" @click="quit">{{ $t('ageButtonNo') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import TweenMax from 'gsap/TweenMax';
import FontFaceObserver from 'fontfaceobserver';
import animations from '@/assets/animations';
import ComposedLogo from '@/components/frame/ComposedLogo.vue';

export default {
  name: 'Loader',

  components: {
    ComposedLogo,
  },

  data() {
    return {
      age: 1980,
      ages: [],
      ageTimeline: null,
      // Load promises from all over the site
      pixiLoadPromise: null,
      ageFilterPromise: null,
      fontPromise: null,
    };
  },

  computed: {
    parsedAge() {
      return new RegExp(/[0-9]{4}/g).test(this.age) ? parseInt(this.age, 10) : false;
    },
  },

  created() {
    // Prepare the listeners for the promises
    this.pixiLoadPromise = new Promise((resolve) => {
      this.$eventHub.$on('pixi-load', resolve);
    });
    this.ageFilterPromise = new Promise((resolve) => {
      this.$eventHub.$on('age-filter', resolve);
    });
    this.fontPromise = new Promise((resolve) => {
      this.$eventHub.$on('font-load', resolve);
    });
    this.dataPromise = new Promise((resolve) => {
      this.$eventHub.$on('data-loaded', resolve);
    });
  },

  mounted() {
    Promise.all([
      this.pixiLoadPromise,
      this.ageFilterPromise,
      this.fontPromise,
      this.dataPromise,
    ]).then(() => {
      this.$eventHub.$emit('load-done');
      this.$store.commit('SET_LOAD', true);
      animations.get('load-done')(this.$el).play();
    });

    const age = this.$el.querySelector('.loader__age');
    TweenMax.set(age, { y: 100, autoAlpha: 0 });
    // if (!localStorage.getItem('age')) {
    this.ageTimeline = animations.get('enter-age')(this.$el);
    this.ageTimeline.play();
    for (let i = new Date().getFullYear(); i >= 1900; i -= 1) {
      this.ages.push(i);
    }
    // } else {
    //   this.$eventHub.$emit('age-filter');
    // }

    const posts = this.$store.dispatch('LOAD_BLOG');
    const bottles = this.$store.dispatch('LOAD_BOTTLES');
    Promise.all([bottles, posts]).then(() => {
      this.$eventHub.$emit('data-loaded');
    });

    const observerGTSuperDisplayNormal = new FontFaceObserver('GT Super Display', {
      weight: 400,
    });
    const observerGTSuperDisplayLight = new FontFaceObserver('GT Super Display', {
      weight: 200,
    });
    const observerIGTFranklingGothicBold = new FontFaceObserver('IGT Frankling Gothic', {
      weight: 700,
    });
    const observerIGTFranklingGothicNormal = new FontFaceObserver('IGT Frankling Gothic', {
      weight: 400,
    });
    Promise.all([
      observerGTSuperDisplayNormal.load(),
      observerGTSuperDisplayLight.load(),
      observerIGTFranklingGothicBold.load(),
      observerIGTFranklingGothicNormal.load(),
    ]).then(() => { this.$eventHub.$emit('font-load'); });
  },

  methods: {
    enter() {
      // if (!this.parsedAge) return;
      // const age = new Date().getFullYear() - this.parsedAge;
      // if (age >= 18) {
      this.$eventHub.$emit('age-filter');
      // localStorage.setItem('age', this.age);
      this.ageTimeline.reverse();
      // }
    },
    quit() {
      document.location = 'https://google.com/';
    },
  },
};
</script>

<style lang="scss" scoped>
.loader {
  @extend %full-fixed;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  background-color: $color-primary;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 99;

  &__logo {
    width: 200px;
  }

  &__age {
    position: absolute;
    top: 55%;
    padding: 5px;

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    @include mq($until: ipadP) {
      top: auto;
      bottom: calc((100vh / 18) * 4);
    }

    &-label {
      margin-bottom: 20px;
      text-align: center;
    }

    &-input {
      position: relative;
      display: flex;
      justify-content: center;

      &::before {
        content: "∙";
        position: absolute;
        right: 67%;
        margin-right: 10px;
        bottom: 5px;
        color: $color-white;
      }

      &::after {
        content: "∙";
        position: absolute;
        left: 65%;
        margin-left: 10px;
        bottom: 5px;
        color: $color-white;
      }
    }

    input {
      width: 100px;
      text-align: center;
      background-color: transparent;
      border: none;
      appearance: none;
      font-family: 'IGT Frankling Gothic';
      font-size: rem(22px);
      font-size: $font-size-default;
      line-height: $font-size-default;
      font-weight: bold;
      padding: 0px;
      -moz-appearance: textfield;

      &::-webkit-inner-spin-button {
        display: none;
      }

      &::placeholder {
        color: $color-white;
        opacity: 0.6;
      }

      &:focus {
        outline: none;
      }
    }

    .buttons {
      display: flex;

      button:first-child {
        margin-right: 15px;
      }

      button:last-child {
        margin-left: 15px;
      }
    }

    &-enter {
      appearance: none;
      background-color: transparent;
      border: 1px solid $color-white;
      border-radius: 50px;
      padding: 15px 60px;
      padding-top: 23px;
      font-family: 'IGT Frankling Gothic';
      font-size: rem(20px);
      font-size: calc(20px * var(--rw));
      line-height: 1;
      text-transform: uppercase;
      margin-top: 80px;
      transition: background-color .6s $inOutCubic,
                  color .6s $inOutCubic;
      cursor: pointer;

      @include mq($until: ipadL) {
        margin-top: 30px;
      }

      &:hover {
        background-color: $color-white;
        color: $color-primary;
      }

      &:focus {
        outline: none;
      }
    }
  }
}
</style>
